import React, { useState } from "react";
import { FiDatabase, FiKey, FiServer, FiUploadCloud } from "react-icons/fi";
import AuthenticationDoc from "./AuthenticationDoc";
import AnalyticsDoc from "./AnalyticsDoc";
import MetadataDoc from "./MetadataDoc";
import FileUploadDoc from "./FileUploadDoc";

type SectionId = "auth" | "analytics" | "metadata" | "file-upload";

const SECTIONS: { id: SectionId; label: string; blurb: string; icon: React.ReactNode }[] = [
  {
    id: "auth",
    label: "Authentication & Environments",
    blurb: "Gateway URLs, OAuth2, required headers",
    icon: <FiKey />,
  },
  {
    id: "analytics",
    label: "Analytics Service",
    blurb: "Pricing, volume, revenue, energy metrics",
    icon: <FiServer />,
  },
  {
    id: "metadata",
    label: "Meta Data",
    blurb: "ISOs, reference data, resources",
    icon: <FiDatabase />,
  },
  {
    id: "file-upload",
    label: "File Center",
    blurb: "SAS-based file upload and download flows",
    icon: <FiUploadCloud />,
  },
];

/**
 * The full API reference for the BETM APIs. This is the source of truth for
 * external and partner integrations - everything an integrator needs to
 * authenticate, call an endpoint and parse the response lives here, so it must
 * be updated in the same change as the API it describes.
 */
const ApiDocumentation: React.FC<{ onOpenMetricDictionary: () => void }> = ({
  onOpenMetricDictionary,
}) => {
  const [active, setActive] = useState<SectionId>("auth");
  const activeSection = SECTIONS.find((s) => s.id === active) ?? SECTIONS[0];

  return (
    <div className="flex flex-col gap-6 p-6 lg:flex-row">
      <nav className="w-full shrink-0 lg:sticky lg:top-4 lg:w-72 lg:self-start" aria-label="API documentation sections">
        <ul className="space-y-1">
          {SECTIONS.map((section) => {
            const isActive = section.id === active;
            return (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => setActive(section.id)}
                  aria-current={isActive ? "page" : undefined}
                  className={`flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition ${
                    isActive
                      ? "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <span className={`mt-0.5 shrink-0 ${isActive ? "text-red-500" : "text-gray-400"}`}>
                    {section.icon}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{section.label}</span>
                    <span className="block text-xs text-gray-500">{section.blurb}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="min-w-0 flex-1">
        <h2 className="mb-1 text-xl font-semibold text-gray-800">{activeSection.label}</h2>
        <p className="mb-6 text-sm text-gray-500">{activeSection.blurb}</p>
        {active === "auth" && <AuthenticationDoc />}
        {active === "analytics" && (
          <AnalyticsDoc onOpenMetricDictionary={onOpenMetricDictionary} />
        )}
        {active === "metadata" && <MetadataDoc />}
        {active === "file-upload" && <FileUploadDoc />}
      </div>
    </div>
  );
};

export default ApiDocumentation;
